import BackendAPI from '~/api/BackendClient/BackendAPI'
import { components } from '~/api/BackendClient/generated-schema'

export type WellPageResponse = components['schemas']['WellPageResponse']
export type WellPageResponseLineage = components['schemas']['WellPageResponseLineage']
export type WellPage_WellCulture = components['schemas']['WellPage_WellCulture']
export type WellPage_CulturePlate = components['schemas']['WellPage_CulturePlate']
export type WellPage_PastObservation =
  components['schemas']['WellPage_WellCulture']['observationHistory'][number]
export type WellPage_PlateImagingObservation =
  components['schemas']['WellPage_PlateImagingObservation']
export type WellPage_Montage = components['schemas']['WellPage_Montage']
export type WellPage_MontageImage = components['schemas']['WellPage_MontageImage']
export type WellCultureStatus = components['schemas']['SystemCultureStatus'] | string

export type WellCulturePastReadObservation =
  components['schemas']['WellPage_PastReadObservation']
export type WellCulturePastImageObservation =
  components['schemas']['WellPage_PastImageObservation']

/**
 * Determines whether the provided observation is a plate imaging observation. Used for type discriminiation
 */
export function isWellImagingObservation(
  observation: WellPage_WellCulture['observationHistory'][number],
): observation is WellCulturePastImageObservation {
  return observation.type === 'plate_imaging'
}

/**
 * Determines whether the provided observation is a plate read observation. Used for type discriminiation
 */
export function isWellReadObservation(
  observation: WellPage_WellCulture['observationHistory'][number],
): observation is WellCulturePastReadObservation {
  return observation.type === 'plate_read'
}

// plate_query_id is the extid by default, but also supports
// the desktop_plate_id for backwards compatibility with the C# backend.
export type WellPageRequest = {
  plate_query_id: string
  well: string
}

export type RecordManualPassageRequest = {
  parent_plate_name: string
  child_plate_name: string
  source_well_to_dest_well_transfers: [string, string][]
}

export type MonitorParseManualPassageDataRequest = {
  data: string
}

export type MonitorParseManualPassageDataResponse =
  | MonitorParseManualPassageDataResponseWorklists
  | MonitorParseManualPassageDataResponseErrors

export type MonitorParseManualPassageDataResponseWorklists = {
  type?: string
  data: MonitorParsedWorklist[]
  culture_plates: MonitorCulturePlate[]
}

export type MonitorParseManualPassageDataResponseErrors = {
  type?: string
  errors: MonitorParseError[]
}

export type MonitorParsedWorklist = {
  plate_barcode: string
  parent_plate_barcode: string
  well_transfers: MonitorWellTransfer[]
}

export type MonitorWellTransfer = {
  parent_well: string
  well: string
}

export type MonitorCulturePlate = {
  user_facing_id: string
  plate_format: string
}

export type MonitorParseError = {
  type: string
  message: string
  line_number?: number | null
  plate_barcode?: string | null
  missing_columns?: string[] | null
}

export type UpdateCultureMetadataRequest = {
  cell_line: string | null
  cell_line_lot: string | null
  passage_number: number | null
}

class WellPageAPI extends BackendAPI {
  getData = (request: WellPageRequest): Promise<WellPageResponse> => {
    return this.client.GET('/api/pages/well-page/', {
      params: {
        query: request,
      },
    })
  }

  updateData = (
    plate_extid: string,
    well: string,
    metadata: UpdateCultureMetadataRequest,
  ): Promise<WellPageResponse> => {
    return this.client.PUT('/api/pages/well-page/{plate_extid}/{well}', {
      params: {
        path: { plate_extid, well },
      },
      body: metadata,
    })
  }

  getCultureLineage = (culture_id: string): Promise<WellPageResponseLineage> => {
    return this.client.GET('/api/pages/well-page/culture-lineage', {
      params: {
        query: { culture_id },
      },
    }) as Promise<WellPageResponseLineage>
  }

  recordManualPassage = async (request: RecordManualPassageRequest): Promise<void> => {
    await this.client.POST('/api/pages/well-page/record-manual-passage', {
      body: request,
    })
  }

  parseManualPassageData = async (
    request: MonitorParseManualPassageDataRequest,
  ): Promise<MonitorParseManualPassageDataResponse> => {
    return this.client.POST('/api/pages/well-page/parse-data', {
      body: request,
    })
  }
}

export default WellPageAPI
